import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  dialogTitle: {
    background: "#0369a1",
    color: "white",
    padding: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: "#e0f2fe",
    border: "1px solid #bae6fd",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoTitle: {
    fontWeight: 600,
    color: "#0369a1",
    marginBottom: 4,
  },
  infoText: {
    color: "#555",
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
  warnBox: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  warnText: {
    color: "#9a3412",
    fontSize: "0.8rem",
    lineHeight: 1.6,
  },
}));

const EMPTY = { baseUrl: "", token: "", app: "appnovo", ativo: true };

const SgpModal = ({ open, onClose, onSave }) => {
  const classes = useStyles();
  const [config, setConfig] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/sgp-integration")
      .then(({ data }) => {
        if (data) {
          setConfig({
            baseUrl: data.baseUrl || "",
            token: data.token || "",
            app: data.app || "appnovo",
            ativo: data.ativo !== undefined ? data.ativo : true,
          });
        } else {
          setConfig(EMPTY);
        }
      })
      .catch(() => setConfig(EMPTY));
  }, [open]);

  const handleClose = () => {
    setConfig(EMPTY);
    setShowToken(false);
    onClose();
  };

  const handleSave = async () => {
    if (config.ativo && !config.baseUrl.trim()) {
      toast.error("Informe a URL base do SGP.");
      return;
    }
    if (config.ativo && !config.token.trim()) {
      toast.error("Informe o Token da API SGP.");
      return;
    }

    try {
      setLoading(true);
      await api.post("/sgp-integration", {
        baseUrl: config.baseUrl.trim(),
        token: config.token.trim(),
        app: (config.app || "appnovo").trim(),
        ativo: config.ativo,
      });
      toast.success("Configuração SGP salva com sucesso!");
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) =>
    setConfig((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle className={classes.dialogTitle}>
        Configurar Integração SGP
      </DialogTitle>

      <DialogContent>
        <Box className={classes.infoBox}>
          <Typography className={classes.infoTitle}>Sobre o SGP</Typography>
          <Typography className={classes.infoText}>
            O SGP (Sistema de Gestão de Provedor) permite consultar clientes por CPF/CNPJ,
            verificar faturas em aberto, gerar 2ª via de boleto/PIX, abrir chamados e
            realizar desbloqueio de acesso. Configure as credenciais da API abaixo.
          </Typography>
        </Box>

        <TextField
          label="URL base do SGP"
          placeholder="https://suaempresa.sgp.net.br"
          value={config.baseUrl}
          onChange={set("baseUrl")}
          fullWidth
          margin="dense"
          variant="outlined"
          helperText="Sem barra no final. Ex: https://gxtelecomparaguassu.sgp.net.br"
        />

        <TextField
          label="Token da API"
          type={showToken ? "text" : "password"}
          value={config.token}
          onChange={set("token")}
          fullWidth
          margin="dense"
          variant="outlined"
          helperText="Gerado em SGP → Administração → Integrações → Tokens"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowToken((v) => !v)} edge="end">
                  {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="App"
          value={config.app}
          onChange={set("app")}
          fullWidth
          margin="dense"
          variant="outlined"
          helperText="Identificador do app configurado no SGP. Padrão: appnovo"
        />

        <Box style={{ marginTop: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.ativo}
                onChange={(e) => setConfig((prev) => ({ ...prev, ativo: e.target.checked }))}
                color="primary"
              />
            }
            label="Ativar integração"
          />
        </Box>

        <Box className={classes.warnBox}>
          <Typography className={classes.warnText}>
            ⚠️ O IP do servidor precisa estar liberado no SGP (Administração → Integrações),
            caso contrário todas as chamadas resultarão em timeout.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Box className={classes.btnWrapper}>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Salvar Configuração
          </Button>
          {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SgpModal;
